import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { createQuery } from "@tanstack/solid-query";
import { firebase } from "@core/firebase/app";
import { queryKeys } from "@core/query";

export type NotificationType = "bookings" | "robots" | "users" | "chats";
export type NotificationSeverity = "info" | "success" | "warning" | "critical";
export type NotificationLinkKind = "booking" | "robot" | "user" | "chat";

export type NotificationLink = {
  kind: NotificationLinkKind;
  id: string;
};

export type NotificationItem = {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  severity: NotificationSeverity;
  createdAt: Date | null;
  metadata: Record<string, unknown>;
  eventId: string | null;
  link: NotificationLink | null;
};

export type NotificationGroup = {
  type: NotificationType;
  items: NotificationItem[];
};

export const NOTIFICATION_TYPES: NotificationType[] = ["bookings", "robots", "users", "chats"];
const NOTIFICATIONS_ROOT = "notifications";

const dismissNotificationCallable = httpsCallable(
  firebase.functions,
  "dismissNotification",
);
const dismissNotificationTypeCallable = httpsCallable(
  firebase.functions,
  "dismissNotificationType",
);
const dismissAllNotificationsCallable = httpsCallable(
  firebase.functions,
  "dismissAllNotifications",
);

const parseLink = (value: unknown): NotificationLink | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const kindRaw = typeof raw.kind === "string" ? raw.kind : typeof raw.type === "string" ? (raw.type as string) : null;
  const id = typeof raw.id === "string" ? raw.id : null;
  if (!kindRaw || !id) {
    return null;
  }
  const kind = kindRaw as NotificationLinkKind;
  if (!["booking", "robot", "user", "chat"].includes(kind)) {
    return null;
  }
  return { kind, id };
};

const mapNotificationDoc = (
  type: NotificationType,
  docSnap: QueryDocumentSnapshot,
): NotificationItem => {
  const data = docSnap.data() as Record<string, unknown>;
  const createdAtValue = data.createdAt;
  const createdAt = createdAtValue instanceof Timestamp ? createdAtValue.toDate() : null;
  const severityRaw = typeof data.severity === "string" ? (data.severity as string) : "info";

  return {
    id: docSnap.id,
    type,
    title: typeof data.title === "string" ? (data.title as string) : "Notification",
    message: typeof data.message === "string" ? (data.message as string) : "",
    severity: ["info", "success", "warning", "critical"].includes(severityRaw)
      ? (severityRaw as NotificationSeverity)
      : "info",
    createdAt,
    metadata: typeof data.metadata === "object" && data.metadata !== null ? (data.metadata as Record<string, unknown>) : {},
    eventId: typeof data.eventId === "string" ? (data.eventId as string) : null,
    link: parseLink(data.link),
  };
};

export const fetchNotifications = async (): Promise<NotificationGroup[]> => {
  const currentUser = firebase.auth.currentUser;
  if (!currentUser) {
    return NOTIFICATION_TYPES.map((type) => ({ type, items: [] }));
  }

  const groupMap = new Map<NotificationType, NotificationItem[]>();
  NOTIFICATION_TYPES.forEach(type => groupMap.set(type, []));

  try {
    for (const type of NOTIFICATION_TYPES) {
      const collectionRef = collection(firebase.firestore, NOTIFICATIONS_ROOT, currentUser.uid, type);

      try {
        const snapshot = await getDocs(collectionRef);

        if (!snapshot.empty) {
          const items = snapshot.docs.map((docSnap: any) => mapNotificationDoc(type, docSnap));
          groupMap.set(type, items);
        }
      } catch (subError: any) {
        // Silently handle errors for individual notification types
      }
    }
  } catch (error) {
    // Silently handle general errors
  }

  const groups: NotificationGroup[] = Array.from(groupMap.entries()).map(([type, items]) => ({
    type,
    items
  }));

  return groups;
};

export const dismissNotification = async (type: NotificationType, notificationId: string): Promise<void> => {
  await dismissNotificationCallable({ type, notificationId });
};

export const dismissNotificationGroup = async (notifications: NotificationItem[]): Promise<void> => {
  // Delete all notifications in the group in parallel
  await Promise.all(
    notifications.map(item => dismissNotification(item.type, item.id))
  );
  console.log(`✅ Dismissed group of ${notifications.length} notifications`);
};

export const dismissNotificationType = async (type: NotificationType): Promise<void> => {
  await dismissNotificationTypeCallable({ type });
};

export const dismissAllNotifications = async (): Promise<void> => {
  await dismissAllNotificationsCallable({});
};

export const useNotificationsQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.notifications.root(),
    queryFn: fetchNotifications,
    retry: false,
  }));
